import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";

const IMAGES = [
  {
    url: "/images/gallery-front-porch.jpg",
    caption: "Welcome to 38885 — our front porch.",
    tag: "Front Porch",
  },
  {
    url: "/images/gallery-front-garden.jpg",
    caption: "A wisteria-shaded bench out front.",
    tag: "Front Garden",
  },
  {
    url: "/images/gallery-courtyard.jpg",
    caption: "The garden fountain and statue by the walkway.",
    tag: "Garden Courtyard",
  },
  {
    url: "/images/gallery-evening-patio.jpg",
    caption: "String-lit patio evenings under the pergola.",
    tag: "Evening Patio",
  },
  {
    url: "/images/gallery-life-at-home.jpg",
    caption: "A quiet afternoon in the garden.",
    tag: "Life at Home",
  },
  {
    url: "/images/gallery-resident-room.jpg",
    caption: "Warm, private resident rooms.",
    tag: "Resident Room",
  },
];

export default function Gallery() {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const show = (i) => {
    setIdx(i);
    setOpen(true);
  };
  const close = () => setOpen(false);
  const prev = () => setIdx((i) => (i - 1 + IMAGES.length) % IMAGES.length);
  const next = () => setIdx((i) => (i + 1) % IMAGES.length);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="reveal text-xs uppercase tracking-[0.3em] font-semibold text-[#667B68]">
            Gallery
          </p>
          <h2
            data-testid="gallery-title"
            className="reveal font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C2A24] mt-4"
            style={{ transitionDelay: "80ms" }}
          >
            A look inside
            <br />
            our home.
          </h2>
          <p
            className="reveal mt-6 text-[17px] leading-relaxed text-[#3E574B] max-w-2xl"
            style={{ transitionDelay: "140ms" }}
          >
            Real spaces, real light, real calm — the kind of place where days
            unfold slowly and everyone has a favorite chair.
          </p>
        </div>

        {/* Masonry-ish grid */}
        <div
          className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          data-testid="gallery-grid"
        >
          {IMAGES.map((img, i) => (
            <button
              key={img.url}
              type="button"
              onClick={() => show(i)}
              data-testid={`gallery-item-${i}`}
              className={`reveal group relative overflow-hidden rounded-2xl bg-[#E5E1D8] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] ${
                i === 0
                  ? "col-span-2 row-span-2 aspect-square md:aspect-[4/5]"
                  : i === 3
                    ? "md:col-span-2 aspect-[4/3]"
                    : "aspect-square"
              }`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <img
                src={img.url}
                alt={img.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
              <span className="absolute top-3 left-3 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase font-semibold bg-white/85 text-[#1C2A24] rounded-full px-3 py-1 backdrop-blur">
                {img.tag}
              </span>
              <span className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/90 text-[#1C2A24] inline-flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all">
                <ImageIcon className="w-4 h-4" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in"
          onClick={close}
        >
          <button
            aria-label="Close"
            onClick={close}
            data-testid="gallery-close-btn"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            aria-label="Previous"
            data-testid="gallery-prev-btn"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-3 sm:left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            aria-label="Next"
            data-testid="gallery-next-btn"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-3 sm:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={IMAGES[idx].url}
              alt={IMAGES[idx].caption}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="mt-4 text-center text-[#F4F1EA]">
              <p className="text-xs tracking-[0.25em] uppercase text-[#A67C52] font-semibold">
                {IMAGES[idx].tag}
              </p>
              <p className="font-heading text-xl sm:text-2xl mt-2">
                {IMAGES[idx].caption}
              </p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/50 mt-3">
                {idx + 1} / {IMAGES.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
